import angular from 'angular';
import '../shared.module';
import { requestData } from '../helpers/http-error.helper';
import { toOrderDetail } from './order.helper';
import { currencyBRL } from '../../core/helpers/string.helper';

const SEARCH_PAGE_SIZE = 20;
const DAY_MS = 24 * 60 * 60 * 1000;

function toProductChoice(record) {
  return {
    id: record.id,
    nome: record.name,
    tipo: record.type,
    reserva: record.booking_mode,
    entregas: record.fulfillment_modes || [],
    variantes: (record.variants || [])
      .filter((variant) => variant.is_active && !variant.deleted_at)
      .map((variant) => ({ value: variant.id, label: variant.name })),
    ofertas: (record.offers || [])
      .filter((offer) => offer.is_active && !offer.deleted_at)
      .map((offer) => ({ value: offer.id, label: offer.name, varianteId: offer.variant_id }))
  };
}

function toSessionChoice(record) {
  const remaining = Math.max(record.capacity - record.reserved - record.sold, 0);
  if (record.status !== 'open' || remaining === 0) return null;
  const when = new Date(record.starts_at).toLocaleString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
  return {
    value: record.id,
    label: `${when}${record.label ? ` · ${record.label}` : ''} · ${remaining} vaga(s)`
  };
}

export function toCharge(record) {
  return {
    id: record.id,
    metodo: record.method,
    status: record.status,
    valor: record.amount_cents / 100,
    pix: record.pix_copy_paste,
    link: record.checkout_url,
    expiraEm: record.expires_at,
    falha: record.failure_reason
  };
}

export function toAgendaSession(record) {
  return {
    id: record.id,
    produtoId: record.product.id,
    produto: record.product.name,
    tipo: record.product.type,
    inicio: record.starts_at,
    fim: record.ends_at,
    rotulo: record.label,
    operador: record.resource ? record.resource.name : null,
    status: record.status,
    capacidade: record.capacity,
    reservadas: record.reserved,
    vendidas: record.sold,
    disponiveis: Math.max(record.capacity - record.reserved - record.sold, 0)
  };
}

class OrderApiService {
  constructor($http, $q, API_BASE_URL) {
    this.$http = $http;
    this.$q = $q;
    this.API_BASE_URL = API_BASE_URL;
  }

  send(request, fallback, map) {
    return requestData(this.$q, request, fallback, map);
  }

  url(path) {
    return `${this.API_BASE_URL}${path}`;
  }

  get(id) {
    return this.send(
      this.$http.get(this.url(`/orders/${id}`)),
      'Não foi possível carregar o pedido.',
      toOrderDetail
    );
  }

  quote(request) {
    return this.send(
      this.$http.post(this.url('/orders/quote'), request, { silentErrors: true }),
      'Não foi possível calcular o pedido.',
      (data) => data
    );
  }

  create(request) {
    return this.send(
      this.$http.post(this.url('/orders'), request),
      'Não foi possível criar o pedido.',
      toOrderDetail
    );
  }

  cancel(id, reason, acceptOpenCharges = false) {
    const body = {
      ...(reason ? { reason } : {}),
      ...(acceptOpenCharges ? { acceptOpenCharges } : {})
    };
    return this.send(
      this.$http.post(this.url(`/orders/${id}/cancel`), body, { silentErrors: true }),
      'Não foi possível cancelar o pedido.',
      toOrderDetail
    );
  }

  changeStatus(id, status) {
    return this.send(
      this.$http.patch(this.url(`/orders/${id}/status`), { status }),
      'Não foi possível alterar o status do pedido.',
      toOrderDetail
    );
  }

  searchContacts(search) {
    return this.send(
      this.$http.get(this.url('/contacts'), {
        params: { search, page: 1, pageSize: SEARCH_PAGE_SIZE }
      }),
      'Não foi possível buscar clientes.',
      (data) =>
        data.items.map((contact) => ({
          value: contact.id,
          label: [contact.name || contact.company_name || 'Sem nome', contact.phone]
            .filter(Boolean)
            .join(' · ')
        }))
    );
  }

  searchProducts(search) {
    return this.send(
      this.$http.get(this.url('/products'), {
        params: {
          search: search || undefined,
          status: 'active',
          page: 1,
          pageSize: SEARCH_PAGE_SIZE,
          sortBy: 'name',
          sortDir: 'asc'
        }
      }),
      'Não foi possível buscar itens do catálogo.',
      (data) => data.items.map((product) => ({ value: product.id, label: product.name }))
    );
  }

  productChoice(productId) {
    const from = new Date();
    const to = new Date(from.getTime() + 90 * DAY_MS);
    return this.$q
      .all([
        this.send(
          this.$http.get(this.url(`/products/${productId}`)),
          'Não foi possível carregar o item.',
          toProductChoice
        ),
        this.send(
          this.$http.get(this.url(`/products/${productId}/option-groups`)),
          'Não foi possível carregar as opções.',
          (data) => data.filter((group) => group.is_active)
        )
      ])
      .then(([product, groups]) => {
        const optionGroups = groups.map((group) => ({
          id: group.id,
          nome: group.name,
          minimo: group.min_select,
          maximo: group.max_select,
          opcoes: group.options
            .filter((option) => option.is_active)
            .map((option) => ({
              id: option.id,
              nome: option.name,
              preco: option.price_cents / 100,
              acrescimo: option.price_cents ? `+ ${currencyBRL(option.price_cents / 100)}` : null
            }))
        }));
        if (product.reserva !== 'session') return { ...product, grupos: optionGroups, sessoes: [] };
        return this.send(
          this.$http.get(this.url(`/products/${productId}/sessions`), {
            params: { from: from.toISOString(), to: to.toISOString() }
          }),
          'Não foi possível carregar a agenda do item.',
          (data) => data.map(toSessionChoice).filter(Boolean)
        ).then((sessions) => ({ ...product, grupos: optionGroups, sessoes: sessions }));
      });
  }

  lookups() {
    return this.$q.all([
      this.send(
        this.$http.get(this.url('/commerce/sales-channels')),
        'Não foi possível carregar os canais.',
        (data) =>
          data
            .filter((channel) => channel.is_active)
            .map((channel) => ({ value: channel.key, label: channel.name }))
      ),
      this.send(
        this.$http.get(this.url('/commerce/tariff-categories')),
        'Não foi possível carregar as tarifas.',
        (data) =>
          data
            .filter((tariff) => tariff.is_active)
            .map((tariff) => ({ value: tariff.id, label: tariff.name }))
      )
    ]);
  }

  createCharge(orderId, payload) {
    return this.send(
      this.$http.post(this.url(`/orders/${orderId}/charges`), payload),
      'Não foi possível gerar a cobrança.',
      toCharge
    );
  }

  sendChargeToConversation(conversationId, paymentId) {
    return this.send(
      this.$http.post(this.url(`/conversations/${conversationId}/charges/${paymentId}/send`), {}),
      'Não foi possível enviar a cobrança na conversa.',
      (data) => data
    );
  }

  refreshCharge(paymentId) {
    return this.send(
      this.$http.post(this.url(`/payments/${paymentId}/refresh`), {}),
      'Não foi possível consultar o pagamento no provedor.',
      toCharge
    );
  }

  sendConfirmation(conversationId, orderId) {
    return this.send(
      this.$http.post(
        this.url(`/conversations/${conversationId}/orders/${orderId}/confirmation`),
        {}
      ),
      'Não foi possível enviar a confirmação ao cliente.',
      (data) => data
    );
  }

  agenda(from, to) {
    return this.send(
      this.$http.get(this.url('/commerce/sessions'), {
        params: { from: from.toISOString(), to: to.toISOString() }
      }),
      'Não foi possível carregar a agenda.',
      (data) => data.map(toAgendaSession)
    );
  }
}

OrderApiService.$inject = ['$http', '$q', 'API_BASE_URL'];

angular.module('app.shared').service('OrderApiService', OrderApiService);

export default OrderApiService;
